if (!customElements.get('nordsign-flag-selector')) {
  customElements.define('nordsign-flag-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.ui = JSON.parse(this.querySelector('[data-flags-ui]')?.textContent || '{}');
      this.form = this.querySelector('[data-filters]');
      this.models = [...this.querySelectorAll('[data-model]')];
      this.families = [...this.querySelectorAll('[data-family]')];
      this.compareButton = this.querySelector('[data-compare]');
      this.dialog = this.querySelector('dialog');
      this.form.hidden = false;
      this.compareButton.hidden = false;
      this.querySelectorAll('[data-compare-control]').forEach(label => { label.hidden = false; });
      this.families.forEach(family => {
        const controls = family.querySelector('[data-switch]');
        if (controls) {
          controls.hidden = false;
          controls.addEventListener('change', event => {
            family.dataset.active = event.target.value;
            this.filter();
          });
        }
        const compare = family.querySelector('[data-compare-systems]');
        if (compare) {
          compare.hidden = false;
          compare.addEventListener('click', () => this.openComparison([...family.querySelectorAll('[data-model]')].sort((a, b) => a.dataset.system === b.dataset.system ? 0 : a.dataset.system === 'Standard' ? -1 : 1), compare));
        }
      });
      this.filter();
      this.form.addEventListener('submit', event => event.preventDefault());
      this.form.addEventListener('change', () => this.filter());
      this.form.addEventListener('reset', () => {
        // Reset controls synchronously before recalculating the visible results.
        this.form.querySelectorAll('select').forEach(select => { select.value = ''; });
        this.filter();
      });
      this.querySelector('[data-clear]').addEventListener('click', () => this.form.reset());
      this.models.forEach(model => model.querySelector('[data-select]').addEventListener('change', () => this.updateComparison()));
      this.compareButton.addEventListener('click', () => this.openComparison(this.selected(), this.compareButton));
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.comparisonTrigger?.focus());
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      });
    }

    text(key, values = {}) {
      const fallback = {
        "result_count": "[count] of [total] flag types",
        "compare_selected": "Compare selected ([count]/5)",
        "model": "Model",
        "system": "System",
        "print": "Print",
        "fabric": "Fabric",
        "pole": "Pole",
        "use": "Use",
        "flag_sizes": "Flag sizes (cm)",
        "display_height": "Display height",
        "pole_length": "Pole length",
        "flag_weight": "Flag weight",
        "pole_weight": "Pole weight",
        "enquiry": "Enquiry",
        "quote": "Get a quote",
        "see_details": "See product details"
      };
      return (this.ui?.[key] || fallback[key] || key).replace(/\[(\w+)\]/g, (token, name) => values[name] ?? token);
    }

    filter() {
      const values = Object.fromEntries(new FormData(this.form));
      let count = 0;
      this.families.forEach(family => {
        const models = [...family.querySelectorAll('[data-model]')];
        const matches = models.filter(model => ['shape', 'sides'].every(key => !values[key] || model.dataset[key] === values[key]) && (values.use !== 'outdoor' || model.dataset.outdoor === 'true'));
        family.hidden = matches.length === 0;
        if (!family.hidden) count += 1;
        const active = matches.find(model => model.dataset.system === family.dataset.active) || matches[0];
        models.forEach(model => { model.hidden = model !== active; });
        family.querySelectorAll('[data-switch] input').forEach(input => {
          input.checked = active?.dataset.system === input.value;
          input.disabled = !matches.some(model => model.dataset.system === input.value);
        });
      });
      this.querySelector('[data-count]').textContent = this.text('result_count', {count, total: this.families.length});
      this.querySelector('[data-empty]').hidden = count !== 0;
      this.querySelector('[data-reset]').disabled = !Object.values(values).some(Boolean);
    }

    selected() {
      return this.models.filter(model => model.querySelector('[data-select]').checked);
    }

    updateComparison() {
      const count = this.selected().length;
      this.compareButton.textContent = this.text('compare_selected', {count});
      this.compareButton.disabled = count < 2;
      this.models.forEach(model => {
        const input = model.querySelector('[data-select]');
        input.disabled = count >= 5 && !input.checked;
      });
    }

    openComparison(selected, trigger) {
      if (selected.length < 2 || selected.length > 5) return;
      this.comparisonTrigger = trigger;
      const table = this.querySelector('[data-comparison-table]');
      table.replaceChildren();
      const head = table.createTHead().insertRow();
      const corner = document.createElement('th');
      corner.scope = 'col';
      corner.textContent = this.text('model');
      head.append(corner);
      selected.forEach(model => {
        const th = document.createElement('th');
        th.scope = 'col';
        const image = model.querySelector('img').cloneNode();
        image.alt = '';
        const link = model.querySelector('[data-model-link]').cloneNode(true);
        link.textContent = model.dataset.productLabel || `${model.dataset.sides} ${model.dataset.system} ${model.dataset.shape} beach flag`;
        th.append(image, link);
        head.append(th);
      });
      const body = table.createTBody();
      const measurements = selected.some(model => model.querySelector('nordsign-drop-config')) ? ['Display height', 'Pole length', 'Flag weight', 'Pole weight'] : [];
      ['System', 'Print', 'Fabric', 'Pole', 'Use', 'Flag sizes (cm)', ...measurements, 'Enquiry'].forEach(label => {
        const row = body.insertRow();
        const heading = document.createElement('th');
        heading.scope = 'row';
        heading.textContent = this.text(label === 'Flag sizes (cm)' ? 'flag_sizes' : label.toLowerCase().replaceAll(' ', '_'));
        row.append(heading);
        selected.forEach(model => {
          const cell = row.insertCell();
          if (label === 'Enquiry') {
            const link = model.querySelector('.ns-flag__quote').cloneNode(true);
            link.textContent = this.text('quote');
            cell.append(link);
          } else if (label === 'Flag sizes (cm)') {
            model.querySelectorAll('tbody tr').forEach(size => {
              const line = document.createElement('div');
              line.textContent = `${size.querySelector('th').textContent}: ${(size.querySelector('[data-dimension]') || size.querySelector('td')).textContent}`;
              cell.append(line);
            });
          } else if (measurements.includes(label)) {
            const values = model.querySelectorAll(`[data-measure="${label}"]`);
            if (!values.length) cell.textContent = this.text('see_details');
            values.forEach(value => {
              const line = document.createElement('div');
              line.textContent = `${value.closest('tr').querySelector('th').textContent}: ${value.textContent}`;
              cell.append(line);
            });
          } else if (label === 'System') {
            cell.textContent = model.dataset.system;
          } else {
            cell.textContent = model.querySelector(`[data-spec="${label}"]`).textContent;
          }
        });
      });
      this.dialog.showModal();
    }
  });
}
